import { useEffect } from 'react'
import { useMe } from '../lib/hooks'

/**
 * Entra SSO gate. In production, SWA's staticwebapp.config.json already
 * gates the app — if you land here without a session cookie, SWA redirects
 * to /.auth/login/aad. This component is the *client-side* fallback:
 *   • Reads /.auth/me via useMe()
 *   • If no principal → redirect to /.auth/login/aad
 *   • If principal → render children
 *
 * In local dev (`func start` + `vite dev`), set VITE_LOCAL_DEV=1 to bypass.
 */
export default function LoginGate({ children }) {
  const bypass = import.meta.env.VITE_LOCAL_DEV === '1'
  const { data: me, isLoading, isError } = useMe()

  useEffect(() => {
    if (bypass) return
    if (isLoading) return
    if (isError || !me) {
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
      window.location.href = `/.auth/login/aad?post_login_redirect_uri=${returnTo}`
    }
  }, [bypass, isLoading, isError, me])

  if (bypass) return children

  if (isLoading) {
    return <SplashScreen message="AUTHENTICATING" />
  }

  if (!me) {
    // Redirect is inflight; show spinner rather than a blank page.
    return <SplashScreen message="REDIRECTING TO SIGN-IN" />
  }

  return children
}

function SplashScreen({ message }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100vh', background: '#0A192F',
      gap: 20, fontFamily: "'Inter', 'Segoe UI', sans-serif",
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 8, flexShrink: 0,
          background: 'linear-gradient(135deg, #D4AF37 0%, #C5A059 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, fontWeight: 800, color: '#0A192F',
        }}>P</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF', letterSpacing: '0.1em' }}>
            PSG DEAL INTELLIGENCE
          </div>
          <div style={{ fontSize: 9, color: '#94A3B8', letterSpacing: '0.18em', marginTop: 2 }}>
            SELF-STORAGE PIPELINE
          </div>
        </div>
      </div>
      <div style={{
        width: 24, height: 24, borderRadius: '50%',
        border: '2px solid #D4AF37', borderTopColor: 'transparent',
        animation: 'spin 0.8s linear infinite',
      }} />
      <p style={{ color: '#94A3B8', fontSize: 11, letterSpacing: '0.16em', margin: 0 }}>
        {message}
      </p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
